// Recovery for the stale-tab-across-deploy failure: a deploy replaces the
// hashed /assets chunks, so a tab still running the previous build 404s when
// it lazy-loads a route ("Failed to fetch dynamically imported module").
// Reloading fetches the fresh index.html, which points at the new hashes;
// so do that once, automatically, instead of showing the error screen.

const RELOAD_STAMP_KEY = 'adj:stale-chunk-reload-at';

// One reload per window: if a second chunk failure lands this soon after a
// reload, the cause isn't a stale tab (e.g. the network or CDN is down), so
// surface the error instead of reload-looping.
const RELOAD_WINDOW_MS = 60_000;

const STALE_CHUNK_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /unable to preload css/i, // Vite's CSS preload helper
];

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return STALE_CHUNK_PATTERNS.some(pattern => pattern.test(message));
}

function lastReloadAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** True while no auto-reload has been attempted within the guard window. */
export function canReloadForStaleChunk(): boolean {
  return Date.now() - lastReloadAt() >= RELOAD_WINDOW_MS;
}

// True once this document has kicked off an auto-reload. location.reload()
// doesn't halt script execution, so the failed import still surfaces follow-up
// errors during teardown; the error boundary uses this to stay blank and skip
// reporting them instead of flashing the error screen.
let reloadInFlight = false;

export function isReloadInFlight(): boolean {
  return reloadInFlight;
}

/**
 * Reload the page once to pick up the new build. Returns false without
 * reloading when the one-shot guard trips, or when sessionStorage is
 * unavailable (no storage means no loop guard, so don't risk it).
 */
export function reloadForStaleChunk(): boolean {
  if (!canReloadForStaleChunk()) return false;
  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch {
    return false;
  }
  reloadInFlight = true;
  window.location.reload();
  return true;
}
